/**
 * Scroll Spy Module
 * File: scroll-spy.js
 * Description: Gestisce lo stato active dei link della sidebar in base alla sezione visibile
 */

const ScrollSpy = {
    navLinks: [],
    sections: [],
    currentActive: null,
    navbar: null,
    adminBar: null,
    observerOptions: {},
    customOffset: 20, // Offset personalizzabile da data attribute

    /**
     * Inizializza lo scroll spy
     * @param {string} linkSelector - Selettore per i link della sidebar
     * @param {string} navbarId - ID della navbar principale
     * @param {string} adminBarId - ID della admin bar WordPress
     */
    initialize(
        linkSelector = "[data-aside-anchor]",
        navbarId = "mainNavbar",
        adminBarId = "wpadminbar"
    ) {
        this.navLinks = document.querySelectorAll(linkSelector);

        if (!this.navLinks.length) {
            console.warn("ScrollSpy: Nessun link trovato con il selettore", linkSelector);
            return;
        }

        this.navbar = document.getElementById(navbarId);
        this.adminBar = document.getElementById(adminBarId);

        // Legge l'offset personalizzato da data attribute (se presente)
        const asideContainer = document.querySelector('[data-sticky-aside]');
        if (asideContainer && asideContainer.dataset.scrollSpyOffset) {
            this.customOffset = parseInt(asideContainer.dataset.scrollSpyOffset, 10);
        }

        // Raccoglie le sezioni corrispondenti ai link
        this.collectSections();

        if (!this.sections.length) {
            console.warn("ScrollSpy: Nessuna sezione trovata");
            return;
        }

        // Imposta il link attivo in base all'hash URL o al primo link
        this.setInitialActiveLink();

        // Configura l'Intersection Observer
        this.setupObserver();

        console.log("ScrollSpy → Inizializzato ✓", this.sections.length, "sezioni");
    },

    /**
     * Raccoglie tutte le sezioni collegate ai link della sidebar
     */
    collectSections() {
        this.sections = [];

        this.navLinks.forEach((link) => {
            const hash = link.getAttribute("href");
            if (!hash || !hash.startsWith("#")) return;

            const sectionId = hash.substring(1);
            const section = document.getElementById(sectionId);

            if (section) {
                this.sections.push({
                    id: sectionId,
                    element: section,
                    link: link,
                });
            }
        });
    },

    /**
     * Imposta il link attivo iniziale basandosi sull'hash URL
     */
    setInitialActiveLink() {
        // Controlla se c'è un hash nell'URL (es. #vantaggi)
        const currentHash = window.location.hash;

        if (currentHash) {
            // Trova il link corrispondente all'hash
            const targetLink = Array.from(this.navLinks).find(
                (link) => link.getAttribute("href") === currentHash
            );

            if (targetLink) {
                // Attiva il link corrispondente all'hash
                this.setActiveLink(targetLink);
                return;
            }
        }

        // Se non c'è hash o non è stato trovato, attiva il primo link
        if (this.navLinks.length > 0) {
            this.setActiveLink(this.navLinks[0]);
        }
    },

    /**
     * Configura il tracking dello scroll
     */
    setupObserver() {
        // Usa scroll listener invece di Intersection Observer per maggiore precisione
        this.attachScrollHandler();
        this.attachClickHandlers();
    },

    /**
     * Gestisce lo scroll per determinare quale sezione è attiva
     */
    attachScrollHandler() {
        let ticking = false;

        const checkActiveSection = () => {
            const navbarHeight = this.navbar ? this.navbar.offsetHeight : 0;
            const adminBarHeight = this.adminBar ? this.adminBar.offsetHeight : 0;
            const scrollPosition = window.scrollY;
            const triggerPoint = navbarHeight + adminBarHeight + this.customOffset;

            // Trova la sezione attualmente visibile
            let activeSection = null;

            // Scorri le sezioni dall'ultima alla prima
            for (let i = this.sections.length - 1; i >= 0; i--) {
                const section = this.sections[i];
                // Calcola la posizione assoluta dal top del documento
                const rect = section.element.getBoundingClientRect();
                const sectionTop = rect.top + scrollPosition;

                // La sezione è attiva se il suo top è sopra o al punto di trigger
                if (scrollPosition + triggerPoint >= sectionTop) {
                    activeSection = section;
                    break;
                }
            }

            // Se non abbiamo trovato nessuna sezione, attiva la prima
            if (!activeSection && this.sections.length > 0) {
                activeSection = this.sections[0];
            }

            // Aggiorna il link attivo
            if (activeSection) {
                this.setActiveLink(activeSection.link);
            }
        };

        // Esegui subito al caricamento
        checkActiveSection();

        // Esegui durante lo scroll con requestAnimationFrame per performance
        window.addEventListener("scroll", () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    checkActiveSection();
                    ticking = false;
                });
                ticking = true;
            }
        });
    },

    /**
     * Imposta il link attivo
     */
    setActiveLink(link) {
        if (this.currentActive === link) return;

        // Rimuove la classe active e il focus da tutti i link
        this.navLinks.forEach((navLink) => {
            navLink.classList.remove("active");
            navLink.blur(); // Rimuove il focus visivo
        });

        // Aggiunge active al link corrente
        if (link) {
            link.classList.add("active");
            this.currentActive = link;
        }
    },

    /**
     * Gestisce il click sui link per smooth scroll
     */
    attachClickHandlers() {
        this.navLinks.forEach((link) => {
            link.addEventListener("click", (e) => {
                const hash = link.getAttribute("href");
                if (!hash || !hash.startsWith("#")) return;

                e.preventDefault();

                const sectionId = hash.substring(1);
                const section = document.getElementById(sectionId);

                if (section) {
                    // Calcola l'offset per lo scroll
                    const navbarHeight = this.navbar ? this.navbar.offsetHeight : 0;
                    const adminBarHeight = this.adminBar ? this.adminBar.offsetHeight : 0;
                    const offset = navbarHeight + adminBarHeight + 40; // allineato a scroll-to-anchor (extraOffset: 40)

                    const elementPosition = section.getBoundingClientRect().top + window.scrollY;
                    const offsetPosition = elementPosition - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                    });

                    // Imposta immediatamente come active
                    this.setActiveLink(link);
                }
            });
        });
    },
};

export default ScrollSpy;
